import { getCSRFToken } from '../utils/getCSRFToken';

const DRAG_THRESHOLD = 6;
const TOUCH_HOLD_MS = 400;
const TOUCH_MOVE_THRESHOLD = 8;
const MOVE_URL = '/bookings_app/move-booking-item/';

function nextIsoDate(isoDate) {
  const [year, month, day] = isoDate.split('-').map(Number);
  const date = new Date(Date.UTC(year, month - 1, day + 1));
  return date.toISOString().slice(0, 10);
}

function eachIsoDate(start, end) {
  const days = [];
  if (!start || !end) return days;
  let cursor = start;
  while (cursor <= end && days.length < 400) {
    days.push(cursor);
    cursor = nextIsoDate(cursor);
  }
  return days;
}

function rangesOverlap(start, end, otherStart, otherEnd) {
  return start <= otherEnd && end >= otherStart;
}

function productUnits(product, productType) {
  if (!product) return [];
  return productType === 'accommodation'
    ? product.accommodation_units || []
    : product.tour_units || [];
}

export const bookingDragMethods = {
  bookingDrag: null,
  bookingDragHover: null,
  bookingDragHints: [],
  bookingDragModal: null,

  canDragBooking(item) {
    if (!item || item.is_temp) return false;
    if (item.booking && item.booking.status === 'CANCELLED') return false;
    return ['Owner', 'Admin', 'Edit'].includes(this.userPermission);
  },

  beginBookingDrag(event, entry, productType, productId) {
    if (!event || event.button !== 0) return;
    if (this.cancelDateSelect) this.cancelDateSelect();
    if (this.bookingDragModal && this.bookingDragModal.open) return;
    const item = entry && entry.item;
    if (!this.canDragBooking(item)) return;

    const drag = {
      item,
      productType,
      productId: String(productId),
      origin: event.currentTarget,
      startX: event.clientX,
      startY: event.clientY,
      pointerId: event.pointerId,
      touch: event.pointerType === 'touch',
      active: false,
      moved: false,
      settled: false,
      startedAt: performance.now(),
      lastX: event.clientX,
      lastY: event.clientY,
      frame: null,
      ghost: null,
      freeUnitKeys: new Set(),
      freeDepartureKeys: new Set(),
      swaps: {},
      swapHint: null,
    };
    const store = this;

    const finish = (pointerEvent) => {
      if (drag.settled) return;
      if (pointerEvent.pointerId !== drag.pointerId) return;
      if (drag.touch && pointerEvent.type === 'pointercancel') return;
      drag.settled = true;
      store._stopBookingDragFrame(drag);
      document.body.classList.remove('booking-drag-selecting');
      document.body.classList.remove('booking-drag-lock');
      window.getSelection()?.removeAllRanges();
      store._clearBookingDragListeners(drag);
      if (drag.touch && !drag.active && drag.moved) {
        store._swallowBookingDragClick();
        return;
      }
      if (!drag.active) return;
      if (pointerEvent.type === 'pointercancel') {
        store._releaseBookingDrag(drag);
        return;
      }
      store._dropBookingDrag(drag, {
        clientX:
          pointerEvent.clientX != null ? pointerEvent.clientX : drag.lastX,
        clientY:
          pointerEvent.clientY != null ? pointerEvent.clientY : drag.lastY,
      });
    };
    const move = (pointerEvent) => {
      if (pointerEvent.pointerId !== drag.pointerId) return;
      const distance = Math.hypot(
        pointerEvent.clientX - drag.startX,
        pointerEvent.clientY - drag.startY,
      );
      if (drag.touch && !drag.active) return;
      if (!drag.touch && pointerEvent.cancelable) pointerEvent.preventDefault();
      if (!drag.active) {
        if (distance < DRAG_THRESHOLD) return;
        drag.active = true;
        store._armBookingDrag(drag);
      }
      drag.lastX = pointerEvent.clientX;
      drag.lastY = pointerEvent.clientY;
      if (drag.touch && pointerEvent.cancelable) pointerEvent.preventDefault();
      window.getSelection()?.removeAllRanges();
      store._moveBookingDrag(drag, pointerEvent);
    };
    drag.move = move;
    drag.finish = finish;
    drag.onTouchMove = (touchEvent) => {
      const point = touchEvent.touches && touchEvent.touches[0];
      if (!point) return;
      drag.lastX = point.clientX;
      drag.lastY = point.clientY;
      if (!drag.active) {
        const elapsed = performance.now() - drag.startedAt;
        const distance = Math.hypot(
          point.clientX - drag.startX,
          point.clientY - drag.startY,
        );
        if (distance >= TOUCH_MOVE_THRESHOLD && elapsed < TOUCH_HOLD_MS) {
          drag.moved = true;
          store._stopBookingDragFrame(drag);
          return;
        }
        if (elapsed < TOUCH_HOLD_MS || drag.moved) return;
        store._gripBookingDrag(drag);
      }
      if (touchEvent.cancelable) touchEvent.preventDefault();
      store._moveBookingDrag(drag, point);
    };
    drag.onTouchEnd = () => {
      finish({
        type: 'pointerup',
        pointerId: drag.pointerId,
      });
    };
    if (drag.touch) {
      const tick = (now) => {
        if (drag.settled || drag.moved || drag.active) return;
        if (now - drag.startedAt >= TOUCH_HOLD_MS) {
          store._gripBookingDrag(drag);
          return;
        }
        drag.frame = window.requestAnimationFrame(tick);
      };
      drag.frame = window.requestAnimationFrame(tick);
      window.addEventListener('touchmove', drag.onTouchMove, {
        passive: false,
      });
      window.addEventListener('touchend', drag.onTouchEnd);
      window.addEventListener('touchcancel', drag.onTouchEnd);
    } else {
      document.body.classList.add('booking-drag-selecting');
      window.getSelection()?.removeAllRanges();
    }
    window.addEventListener('pointermove', move, { passive: false });
    window.addEventListener('pointerup', finish);
    window.addEventListener('pointercancel', finish);
  },

  _gripBookingDrag(drag) {
    drag.active = true;
    document.body.classList.add('booking-drag-selecting');
    document.body.classList.add('booking-drag-lock');
    window.getSelection()?.removeAllRanges();
    this._armBookingDrag(drag);
    if (drag.ghost) drag.ghost.classList.add('is-gripped');
  },

  _swallowBookingDragClick() {
    const swallow = (clickEvent) => {
      clickEvent.preventDefault();
      clickEvent.stopPropagation();
      window.removeEventListener('click', swallow, true);
    };
    window.addEventListener('click', swallow, true);
  },

  _stopBookingDragFrame(drag) {
    if (!drag || !drag.frame) return;
    window.cancelAnimationFrame(drag.frame);
    drag.frame = null;
  },

  _clearBookingDragListeners(drag) {
    this._stopBookingDragFrame(drag);
    window.removeEventListener('pointermove', drag.move);
    window.removeEventListener('pointerup', drag.finish);
    window.removeEventListener('pointercancel', drag.finish);
    if (drag.onTouchMove) {
      window.removeEventListener('touchmove', drag.onTouchMove);
    }
    if (drag.onTouchEnd) {
      window.removeEventListener('touchend', drag.onTouchEnd);
      window.removeEventListener('touchcancel', drag.onTouchEnd);
    }
  },

  _armBookingDrag(drag) {
    const rect = drag.origin.getBoundingClientRect();
    const label = drag.origin.querySelector('.calendar-booking-item-label');
    const ghost = document.createElement('div');
    ghost.className = `${drag.origin.className} booking-drag-ghost`;
    ghost.textContent = (
      (label && label.textContent) ||
      drag.origin.textContent ||
      ''
    ).trim();
    ghost.style.position = 'fixed';
    ghost.style.margin = '0';
    ghost.style.transform = 'none';
    ghost.style.pointerEvents = 'none';
    ghost.style.zIndex = '5000';
    ghost.style.width = `${rect.width}px`;
    ghost.style.height = `${rect.height}px`;
    ghost.style.left = `${rect.left}px`;
    ghost.style.top = `${rect.top}px`;
    document.body.appendChild(ghost);
    drag.ghost = ghost;
    drag.grabOffsetY = drag.startY - rect.top;
    drag.origin.classList.add('booking-drag-origin');
    this._setBookingPreviewTooltips(false);
    this._prepareBookingDragTargets(drag);
    const suppressClick = (clickEvent) => {
      clickEvent.preventDefault();
      clickEvent.stopPropagation();
      window.removeEventListener('click', suppressClick, true);
    };
    window.addEventListener('click', suppressClick, true);
    this.bookingDrag = drag;
  },

  _moveBookingDrag(drag, pointerEvent) {
    if (drag.ghost) {
      drag.ghost.style.top = `${pointerEvent.clientY - drag.grabOffsetY}px`;
    }
    const swapGroup = drag.ghost
      ? this._swapGroupUnderDrag(drag, drag.ghost.getBoundingClientRect())
      : null;
    if (swapGroup) {
      const ok = this._groupCanSwap(drag, swapGroup.items);
      const signature = `${ok}|${swapGroup.items
        .map((item) => item.id)
        .join(',')}`;
      if (drag.swapSignature !== signature) {
        drag.swapSignature = signature;
        this.bookingDragHints = swapGroup.chips.map((chip) => {
          const rect = chip.getBoundingClientRect();
          return {
            id: chip.dataset.bookingItemId,
            x: rect.left + rect.width / 2 - 11,
            y: rect.top + rect.height / 2 - 11,
            ok,
          };
        });
      }
      drag.swapOk = ok;
      if (drag.hoverKey) {
        drag.hoverKey = '';
        this.bookingDragHover = null;
      }
      return;
    }
    drag.swapSignature = '';
    drag.swapOk = false;
    if (this.bookingDragHints.length) this.bookingDragHints = [];

    const hit = document.elementFromPoint(
      pointerEvent.clientX,
      pointerEvent.clientY,
    );
    const row = hit && hit.closest('[data-drop-product]');
    let hover = null;
    if (row && row.dataset.dropProductType === drag.productType) {
      const productId = String(row.dataset.dropProduct);
      const unitId = row.dataset.dropUnit || '';
      const departureId = row.dataset.dropDeparture || '';
      const allowed = unitId
        ? drag.freeUnitKeys.has(`${productId}|${unitId}|${departureId}`)
        : drag.freeDepartureKeys.has(`${productId}|${departureId}`);
      if (allowed) hover = { productId, unitId, departureId };
    }
    const hoverKey = hover
      ? `${hover.productId}|${hover.unitId}|${hover.departureId}`
      : '';
    if (hoverKey !== drag.hoverKey) {
      drag.hoverKey = hoverKey;
      this.bookingDragHover = hover;
    }
  },

  _overlappingBookingChips(drag, ghostRect) {
    const matches = [];
    document.querySelectorAll('[data-booking-item-id]').forEach((chip) => {
      if (
        chip.classList.contains('booking-drag-ghost') ||
        chip.classList.contains('booking-drag-origin')
      ) {
        return;
      }
      const chipId = chip.dataset.bookingItemId;
      if (!chipId || chipId === String(drag.item.id)) return;
      if (!this._bookingOnProduct(chipId, drag.productType, drag.productId)) {
        return;
      }
      const rect = chip.getBoundingClientRect();
      const width =
        Math.min(ghostRect.right, rect.right) -
        Math.max(ghostRect.left, rect.left);
      const height =
        Math.min(ghostRect.bottom, rect.bottom) -
        Math.max(ghostRect.top, rect.top);
      if (width <= 4 || height <= 4) return;
      matches.push({ chip, chipId, area: width * height });
    });
    return matches;
  },

  _bookingRecord(bookingItemId) {
    const entry = this.bookingItems.find(
      (candidate) =>
        candidate.item && String(candidate.item.id) === String(bookingItemId),
    );
    return entry ? entry.item : null;
  },

  _swapGroupUnderDrag(drag, ghostRect) {
    const originUnit = String(
      drag.item.accommodation_unit || drag.item.tour_unit || '',
    );
    const originDeparture = this.departureId(drag.item.tour_departure);
    const groups = new Map();
    this._overlappingBookingChips(drag, ghostRect).forEach((match) => {
      const item = this._bookingRecord(match.chipId);
      if (!item || item.is_temp) return;
      const unitId = item.accommodation_unit || item.tour_unit;
      const departureId = this.departureId(item.tour_departure);
      if (
        !unitId ||
        (String(unitId) === originUnit && departureId === originDeparture)
      ) {
        return;
      }
      const key = `${unitId}|${departureId || ''}`;
      if (!groups.has(key)) {
        groups.set(key, { items: [], chips: [], area: 0 });
      }
      const group = groups.get(key);
      if (
        group.items.some((existing) => String(existing.id) === String(item.id))
      ) {
        return;
      }
      group.items.push(item);
      group.chips.push(match.chip);
      group.area += match.area;
    });
    let best = null;
    groups.forEach((group) => {
      if (!best || group.area > best.area) best = group;
    });
    return best && best.items.length ? best : null;
  },

  _groupCanSwap(drag, items) {
    const targetUnit = items[0].accommodation_unit || items[0].tour_unit;
    const targetDeparture = this.departureId(items[0].tour_departure);
    const originUnit = drag.item.accommodation_unit || drag.item.tour_unit;
    const originDeparture = this.departureId(drag.item.tour_departure);
    const samePlace = items.every((item) => {
      const unitId = item.accommodation_unit || item.tour_unit;
      return (
        unitId === targetUnit &&
        this.departureId(item.tour_departure) === targetDeparture
      );
    });
    if (!samePlace) return false;
    const ignoreIds = new Set([
      String(drag.item.id),
      ...items.map((item) => String(item.id)),
    ]);
    if (
      !this._unitAcceptsBooking(
        targetUnit,
        drag.item,
        ignoreIds,
        targetDeparture,
      )
    ) {
      return false;
    }
    return items.every((item) =>
      this._unitAcceptsBooking(originUnit, item, ignoreIds, originDeparture),
    );
  },

  _dropBookingDrag(drag, pointerEvent) {
    const swapGroup = drag.ghost
      ? this._swapGroupUnderDrag(drag, drag.ghost.getBoundingClientRect())
      : null;
    const hit = document.elementFromPoint(
      pointerEvent.clientX,
      pointerEvent.clientY,
    );
    const row = hit && hit.closest('[data-drop-product]');
    this._releaseBookingDrag(drag);
    if (swapGroup) {
      if (this._groupCanSwap(drag, swapGroup.items)) {
        const swapIds = swapGroup.items.map((item) => String(item.id));
        const payload = {
          booking_item_id: drag.item.id,
          swap_booking_item_ids: swapIds,
        };
        const touchesChannelManager =
          drag.item.channel_manager ||
          swapGroup.items.some((item) => item.channel_manager);
        if (touchesChannelManager) {
          this._openBookingDragOtaRoom(drag, payload);
        } else {
          this._submitBookingDrag(payload);
        }
      }
      return;
    }

    if (!row || row.dataset.dropProductType !== drag.productType) return;
    const productId = String(row.dataset.dropProduct);
    const unitId = row.dataset.dropUnit || null;
    const departureId = row.dataset.dropDeparture || null;
    const allowed = unitId
      ? drag.freeUnitKeys.has(`${productId}|${unitId}|${departureId || ''}`)
      : drag.freeDepartureKeys.has(`${productId}|${departureId}`);
    if (!allowed) return;

    const payload = {
      booking_item_id: drag.item.id,
      target_unit_id: unitId,
      target_departure_id: departureId,
    };
    if (productId === drag.productId) {
      if (drag.item.channel_manager) {
        this._openBookingDragOtaRoom(drag, payload);
      } else {
        this._submitBookingDrag(payload);
      }
      return;
    }
    this._openBookingDragModal(drag, productId, unitId, departureId);
  },

  _releaseBookingDrag(drag) {
    if (drag.ghost) {
      drag.ghost.remove();
      drag.ghost = null;
    }
    if (drag.origin) drag.origin.classList.remove('booking-drag-origin');
    this._setBookingPreviewTooltips(true);
    this.bookingDragHints = [];
    this.bookingDragHover = null;
    this.bookingDrag = null;
  },

  _setBookingPreviewTooltips(enabled) {
    document
      .querySelectorAll('[data-tippy-theme="booking-card"]')
      .forEach((element) => {
        const tooltip = element._tippy;
        if (!tooltip) return;
        if (enabled) {
          tooltip.enable();
          return;
        }
        tooltip.hide();
        tooltip.disable();
      });
  },

  _prepareBookingDragTargets(drag) {
    const item = drag.item;
    const originUnit = item.accommodation_unit || item.tour_unit;
    const originDeparture = this.departureId(item.tour_departure);
    const products =
      drag.productType === 'accommodation' ? this.accommodations : this.tours;
    const ignoreSelf = new Set([String(item.id)]);

    products.forEach((product) => {
      const productId = String(product.id);
      const units = productUnits(product, drag.productType);
      const scheduled = drag.productType === 'tour' && product.is_scheduled;
      if (!scheduled) {
        units.forEach((unit) => {
          if (productId === drag.productId && unit.id === originUnit) return;
          if (this._unitAcceptsBooking(unit.id, item, ignoreSelf, null)) {
            drag.freeUnitKeys.add(`${productId}|${unit.id}|`);
          }
        });
        return;
      }

      this.getVisibleDepartures(product).forEach((departure) => {
        const freeUnits = units.filter((unit) => {
          if (
            productId === drag.productId &&
            unit.id === originUnit &&
            departure.id === originDeparture
          ) {
            return false;
          }
          return this._unitAcceptsBooking(
            unit.id,
            item,
            ignoreSelf,
            departure.id,
          );
        });
        freeUnits.forEach((unit) => {
          drag.freeUnitKeys.add(`${productId}|${unit.id}|${departure.id}`);
        });
        const sameSlot =
          productId === drag.productId && departure.id === originDeparture;
        if (!sameSlot && freeUnits.length) {
          drag.freeDepartureKeys.add(`${productId}|${departure.id}`);
        }
      });
    });

    const originProduct = products.find(
      (product) => String(product.id) === drag.productId,
    );
    const originUnitIds = new Set(
      productUnits(originProduct, drag.productType).map((unit) => unit.id),
    );
    this.bookingItems.forEach((entry) => {
      const other = entry.item;
      if (!other || other.id === item.id || other.is_temp) return;
      const otherUnit = other.accommodation_unit || other.tour_unit;
      if (!originUnitIds.has(otherUnit)) return;
      const otherDeparture = this.departureId(other.tour_departure);
      if (otherUnit === originUnit && otherDeparture === originDeparture)
        return;
      const ignoreBoth = new Set([String(item.id), String(other.id)]);
      const itemFits = this._unitAcceptsBooking(
        otherUnit,
        item,
        ignoreBoth,
        otherDeparture,
      );
      const otherFits = this._unitAcceptsBooking(
        originUnit,
        other,
        ignoreBoth,
        originDeparture,
      );
      drag.swaps[other.id] = itemFits && otherFits;
    });
  },

  _unitAcceptsBooking(unitId, item, ignoreIds, departureId) {
    if (this._unitMissingRate(unitId, item.start_date, item.end_date))
      return false;
    return !this._unitBusy(
      unitId,
      item.start_date,
      item.end_date,
      ignoreIds,
      departureId,
    );
  },

  _unitMissingRate(unitId, start, end) {
    const rates = this.calendarDatesWithRates[unitId];
    if (!rates) return false;
    return eachIsoDate(start, end).some((day) => rates[day] === false);
  },

  _unitBusy(unitId, start, end, ignoreIds, departureId) {
    const bookingHit = this.bookingItems.some((entry) => {
      const other = entry.item;
      if (!other || ignoreIds.has(String(other.id))) return false;
      const onUnit =
        other.accommodation_unit === unitId || other.tour_unit === unitId;
      if (!onUnit) return false;
      if (!rangesOverlap(other.start_date, other.end_date, start, end))
        return false;
      if (!departureId) return true;
      const otherDeparture = this.departureId(other.tour_departure);
      return !otherDeparture || otherDeparture === departureId;
    });
    if (bookingHit) return true;

    const blockHit = this.blockItems.some((entry) => {
      const block = entry.item;
      if (!block) return false;
      const onUnit =
        block.accommodation_unit === unitId || block.tour_unit === unitId;
      if (!onUnit) return false;
      if (!rangesOverlap(block.start_date, block.end_date, start, end))
        return false;
      if (!departureId) return true;
      const blockDeparture = this.departureId(block.tour_departure);
      return !blockDeparture || blockDeparture === departureId;
    });
    if (blockHit) return true;

    const blocked = this.calendarDatesBlocked[unitId];
    if (!blocked || !blocked.dates) return false;
    return eachIsoDate(start, end).some((day) => {
      const ids = blocked.dates[day] || [];
      const departures = (blocked.departures && blocked.departures[day]) || [];
      return ids.some((bookingId, index) => {
        if (ignoreIds.has(String(bookingId))) return false;
        if (!departureId) return true;
        const blockedDeparture = departures[index];
        return blockedDeparture == null || blockedDeparture === departureId;
      });
    });
  },

  _bookingOnProduct(bookingItemId, productType, productId) {
    const products =
      productType === 'accommodation' ? this.accommodations : this.tours;
    const product = products.find(
      (candidate) => String(candidate.id) === String(productId),
    );
    const unitIds = new Set(
      productUnits(product, productType).map((unit) => unit.id),
    );
    const entry = this.bookingItems.find(
      (candidate) => candidate.item && candidate.item.id === bookingItemId,
    );
    if (!entry) return false;
    const unit = entry.item.accommodation_unit || entry.item.tour_unit;
    return unitIds.has(unit);
  },

  departureShowsUnitRows(tour, departureId) {
    if (!tour || !tour.is_scheduled) return false;
    const units = Number(tour.total_active_units) || 0;
    if (!units) return false;
    const chips = this.departureChips(tour, departureId);
    const hasChips = chips.bookings.length + chips.blocks.length > 0;
    return hasChips && chips.laneCount >= units;
  },

  departureRowPixelHeight(tour, departureId) {
    if (
      !this.isProductCollapsed(tour.id) &&
      this.departureShowsUnitRows(tour, departureId)
    ) {
      const units =
        (tour.tour_units || []).length || Number(tour.total_active_units) || 1;
      return units * 48;
    }
    return this.getDepartureRowHeight(tour, departureId);
  },

  isBookingDropTarget(productId, unitId, departureId) {
    const hover = this.bookingDragHover;
    if (!hover || String(hover.productId) !== String(productId)) return false;
    return (
      (hover.unitId || '') === String(unitId || '') &&
      (hover.departureId || '') === String(departureId || '')
    );
  },

  bookingItemsForDepartureUnit(unitId, departureId) {
    return this.getBookingItemsForUnit(unitId).filter(
      (entry) =>
        this.departureId(entry.item && entry.item.tour_departure) ===
        departureId,
    );
  },

  blockItemsForDepartureUnit(unitId, departureId) {
    return this.getBlockItemsForUnit(unitId).filter((entry) => {
      const block = entry.item;
      if (!block) return false;
      const blockDeparture = this.departureId(block.tour_departure);
      return !blockDeparture || blockDeparture === departureId;
    });
  },

  scheduledUnitDateClass(unitId, departureId, date) {
    const rates = this.calendarDatesWithRates[unitId];
    if (rates && rates[date] === false) return 'no-rate section-fade-in';
    if (this._unitBusy(unitId, date, date, new Set(), departureId)) {
      return 'has-rate unavailable section-fade-in';
    }
    return 'has-rate available section-fade-in';
  },

  scheduledUnitDateAvailable(unitId, departureId, date) {
    return (
      this.scheduledUnitDateClass(unitId, departureId, date).indexOf(
        'available',
      ) !== -1
    );
  },

  async _submitBookingDrag(payload, modal) {
    if (modal) modal.saving = true;
    // The move broadcasts update_booking. Skip that echo and reload items
    // once here, instead of reloading the whole grid.
    this.skipNextWebSocketUpdate = true;
    try {
      const response = await fetch(MOVE_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': getCSRFToken(),
        },
        body: JSON.stringify(payload),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        this.skipNextWebSocketUpdate = false;
        const message = data.error || 'This booking stayed in place.';
        if (modal) {
          modal.error = message;
          modal.saving = false;
          this.bookingDragModal = modal;
        } else if (this.toastStore) {
          this.toastStore.addToast(message, 'danger');
        }
        return false;
      }
      if (modal) this.bookingDragModal = null;
      await this.requestItemsRefresh();
      return true;
    } catch (error) {
      this.skipNextWebSocketUpdate = false;
      const message = 'This booking stayed in place.';
      if (modal) {
        modal.error = message;
        modal.saving = false;
        this.bookingDragModal = modal;
      } else if (this.toastStore) {
        this.toastStore.addToast(message, 'danger');
      }
      return false;
    }
  },

  _openBookingDragModal(drag, productId, unitId, departureId) {
    const products =
      drag.productType === 'accommodation' ? this.accommodations : this.tours;
    const product = products.find(
      (candidate) => String(candidate.id) === String(productId),
    );
    const listings = (product && product.rate_set) || [];
    const currentId = drag.item.rate_set_id;
    const hasCurrent = listings.some(
      (listing) => String(listing.id) === String(currentId),
    );
    this.bookingDragModal = {
      open: true,
      item: drag.item,
      product,
      productType: drag.productType,
      targetUnitId: unitId,
      targetDepartureId: departureId,
      listings,
      selectedListingId: hasCurrent ? String(currentId) : '',
      listingDetail: null,
      mode: 'cross',
      pendingPayload: null,
      priceChoice: drag.item.channel_manager ? 'lock' : '',
      repriceAmount: null,
      repriceLoading: false,
      priceRequestId: 0,
      amountPeople: '',
      amountAdults: '',
      amountChildren: '',
      error: '',
      saving: false,
    };
  },

  _openBookingDragOtaRoom(drag, payload) {
    this.bookingDragModal = {
      open: true,
      mode: 'ota-room',
      item: drag.item,
      pendingPayload: payload,
      error: '',
      saving: false,
    };
  },

  bookingDragIsRoomConfirm() {
    return this.bookingDragModal && this.bookingDragModal.mode === 'ota-room';
  },

  bookingDragOtaWarning() {
    return Boolean(
      this.bookingDragModal &&
        this.bookingDragModal.item &&
        this.bookingDragModal.item.channel_manager,
    );
  },

  bookingDragPeopleLabel() {
    const detail = this.bookingDragModal && this.bookingDragModal.listingDetail;
    const label = detail && detail.person_label_plural;
    return (label && String(label).trim()) || 'People';
  },

  bookingDragMoney(amount) {
    if (amount === null || amount === undefined || amount === '') return '';
    const value = Number(amount);
    if (Number.isNaN(value)) return '';
    const currency =
      document.querySelector('.calendar-container')?.dataset.currency || 'EUR';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  },

  bookingDragLockPriceLabel() {
    const amount = this.bookingDragMoney(
      this.bookingDragModal?.item?.final_price,
    );
    return amount ? `Keep ${amount}` : 'Keep the current price';
  },

  bookingDragRepriceLabel() {
    const modal = this.bookingDragModal;
    if (!modal) return '';
    const peopleLabel = this.bookingDragPeopleLabel().toLowerCase();
    if (this.bookingDragNeedsPeople() && modal.amountPeople === '') {
      return `Choose ${peopleLabel} to see this price`;
    }
    if (
      this.bookingDragNeedsPersonTypes() &&
      (modal.amountAdults === '' || modal.amountChildren === '')
    ) {
      return 'Choose adults and children to see this price';
    }
    if (modal.repriceLoading) return 'Calculating…';
    const amount = this.bookingDragMoney(modal.repriceAmount);
    return amount
      ? `Update to ${amount}`
      : 'Recalculate with this listing’s current rates';
  },

  async refreshBookingDragPrices() {
    const modal = this.bookingDragModal;
    if (!modal) return;
    modal.priceRequestId = (modal.priceRequestId || 0) + 1;
    const requestId = modal.priceRequestId;
    modal.repriceAmount = null;
    const waitingForPeople =
      (this.bookingDragNeedsPeople() && modal.amountPeople === '') ||
      (this.bookingDragNeedsPersonTypes() &&
        (modal.amountAdults === '' || modal.amountChildren === ''));
    if (
      !this.bookingDragShowsPriceChoice() ||
      this.bookingDragDurationBlocked() ||
      waitingForPeople
    ) {
      modal.repriceLoading = false;
      this.bookingDragModal = { ...modal };
      return;
    }
    modal.repriceLoading = true;
    this.bookingDragModal = { ...modal };
    const product =
      modal.productType === 'accommodation' ? 'Accommodation' : 'Tour';
    const endDate =
      product === 'Accommodation' && modal.item.end_date
        ? nextIsoDate(modal.item.end_date)
        : modal.item.end_date;
    const detail = modal.listingDetail;
    let selectedPeople = Number(modal.item.amount_people || 0);
    if (detail && this._bookingDragPersonUnit(detail)) {
      selectedPeople = 1;
    } else if (this.bookingDragNeedsPeople()) {
      selectedPeople = Number(modal.amountPeople);
    } else if (this.bookingDragNeedsPersonTypes()) {
      selectedPeople =
        Number(modal.amountAdults) + Number(modal.amountChildren);
    } else if (detail && !detail.people_as_option && !detail.use_person_types) {
      selectedPeople = 0;
    }
    try {
      const response = await fetch('/bookings_app/calculate-price/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': getCSRFToken(),
        },
        body: JSON.stringify({
          product,
          startDate: modal.item.start_date,
          endDate,
          soldAs: modal.selectedListingId,
          units: 1,
          selectedPeople,
          bookingItemId: modal.item.id,
          completeReprice: true,
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (
        !this.bookingDragModal ||
        this.bookingDragModal.priceRequestId !== requestId
      ) {
        return;
      }
      this.bookingDragModal.repriceLoading = false;
      this.bookingDragModal.repriceAmount = response.ok ? data.price : null;
      this.bookingDragModal = { ...this.bookingDragModal };
    } catch (error) {
      if (
        !this.bookingDragModal ||
        this.bookingDragModal.priceRequestId !== requestId
      ) {
        return;
      }
      this.bookingDragModal.repriceLoading = false;
      this.bookingDragModal.repriceAmount = null;
      this.bookingDragModal = { ...this.bookingDragModal };
    }
  },

  bookingDragShowsPriceChoice() {
    const modal = this.bookingDragModal;
    if (!modal || !modal.selectedListingId) return false;
    return (
      String(modal.selectedListingId) !== String(modal.item.rate_set_id || '')
    );
  },

  bookingDragDurationBlocked() {
    const modal = this.bookingDragModal;
    if (!modal || !this.bookingDragShowsPriceChoice()) return false;
    const detail = modal.listingDetail;
    return Boolean(
      detail &&
        detail.date_select === 'single' &&
        modal.item.start_date !== modal.item.end_date,
    );
  },

  bookingDragNeedsPeople() {
    if (this.bookingDragModal?.item?.channel_manager) return false;
    const detail = this._bookingDragListingDetail();
    if (!detail || detail.use_person_types) return false;
    if (this._bookingDragPersonUnit(detail)) return false;
    return Boolean(detail.people_as_option);
  },

  bookingDragNeedsPersonTypes() {
    if (this.bookingDragModal?.item?.channel_manager) return false;
    const detail = this._bookingDragListingDetail();
    if (!detail || !detail.use_person_types) return false;
    if (this._bookingDragPersonUnit(detail)) return false;
    return true;
  },

  bookingDragPersonUnitNote() {
    const detail = this._bookingDragListingDetail();
    if (!detail || !this._bookingDragPersonUnit(detail)) return '';
    const people = Number(this.bookingDragModal.item.amount_people || 0);
    if (people === 1) return '';
    return 'This listing books one person.';
  },

  bookingDragPeopleOptions() {
    const detail = this.bookingDragModal && this.bookingDragModal.listingDetail;
    const maximum = Math.max(1, Number(detail && detail.people_per_unit) || 1);
    return Array.from({ length: maximum }, (_value, index) => index + 1);
  },

  bookingDragCountOptions() {
    const detail = this.bookingDragModal && this.bookingDragModal.listingDetail;
    const maximum = Math.max(1, Number(detail && detail.people_per_unit) || 1);
    return Array.from({ length: maximum + 1 }, (_value, index) => index);
  },

  bookingDragConfirmDisabled() {
    const modal = this.bookingDragModal;
    if (!modal || modal.saving) return true;
    if (modal.mode === 'ota-room') return false;
    if (!modal.selectedListingId) return true;
    if (this.bookingDragShowsPriceChoice() && !modal.priceChoice) return true;
    if (this.bookingDragDurationBlocked()) return true;
    if (this.bookingDragNeedsPeople() && modal.amountPeople === '') return true;
    if (this.bookingDragNeedsPersonTypes()) {
      if (modal.amountAdults === '' || modal.amountChildren === '') return true;
      const total = Number(modal.amountAdults) + Number(modal.amountChildren);
      const maximum =
        Number(modal.listingDetail && modal.listingDetail.people_per_unit) || 0;
      if (total < 1 || (maximum > 0 && total > maximum)) return true;
    }
    if (
      this.bookingDragShowsPriceChoice() &&
      !modal.listingDetail &&
      String(modal.selectedListingId) !== String(modal.item.rate_set_id || '')
    ) {
      return true;
    }
    return false;
  },

  _bookingDragListingDetail() {
    const modal = this.bookingDragModal;
    if (!modal || !this.bookingDragShowsPriceChoice()) return null;
    return modal.listingDetail;
  },

  _bookingDragPersonUnit(detail) {
    return ['Person', 'Adult', 'Child'].includes(detail.unit_type);
  },

  async onBookingDragListingChange(listingId) {
    const modal = this.bookingDragModal;
    if (!modal) return;
    modal.selectedListingId = listingId;
    modal.priceChoice = modal.item.channel_manager ? 'lock' : '';
    modal.amountPeople = '';
    modal.amountAdults = '';
    modal.amountChildren = '';
    modal.error = '';
    modal.listingDetail = null;
    this.bookingDragModal = modal;
    if (
      !listingId ||
      String(listingId) === String(modal.item.rate_set_id || '')
    ) {
      return;
    }
    try {
      const response = await fetch(`/rates/rate-set/${listingId}/`);
      const detail = await response.json();
      if (!response.ok) {
        modal.error = 'That listing could not be loaded.';
      } else {
        modal.listingDetail = detail;
        this._seedBookingDragOccupancy(modal, detail);
      }
    } catch (error) {
      modal.error = 'That listing could not be loaded.';
    }
    this.bookingDragModal = { ...modal };
    if (modal.item.channel_manager) return;
    this.refreshBookingDragPrices();
  },

  _seedBookingDragOccupancy(modal, detail) {
    if (!detail || this._bookingDragPersonUnit(detail)) return;
    const maximum = Number(detail.people_per_unit) || 0;
    if (detail.use_person_types) {
      const adults = Number(modal.item.amount_adults || 0);
      const children = Number(modal.item.amount_children || 0);
      const total = adults + children;
      if (total >= 1 && (!maximum || total <= maximum)) {
        modal.amountAdults = String(adults);
        modal.amountChildren = String(children);
      }
      return;
    }
    if (!detail.people_as_option) return;
    const people = Number(modal.item.amount_people || 0);
    if (people >= 1 && (!maximum || people <= maximum)) {
      modal.amountPeople = String(people);
    }
  },

  setBookingDragPriceChoice(choice) {
    if (!this.bookingDragModal) return;
    this.bookingDragModal.priceChoice = choice;
    this.bookingDragModal = { ...this.bookingDragModal };
  },

  cancelBookingDrag() {
    this.bookingDragModal = null;
  },

  confirmBookingDrag() {
    const modal = this.bookingDragModal;
    if (!modal || this.bookingDragConfirmDisabled()) return;
    if (modal.mode === 'ota-room') {
      this._submitBookingDrag(modal.pendingPayload, modal);
      return;
    }
    const listingChanged = this.bookingDragShowsPriceChoice();
    const payload = {
      booking_item_id: modal.item.id,
      target_unit_id: modal.targetUnitId,
      target_departure_id: modal.targetDepartureId,
      rate_set_id: modal.selectedListingId,
    };
    if (modal.item.channel_manager && listingChanged) {
      payload.lock_price = true;
      payload.complete_reprice = false;
    } else if (listingChanged) {
      payload.lock_price = modal.priceChoice === 'lock';
      payload.complete_reprice = modal.priceChoice === 'reprice';
      if (this.bookingDragNeedsPeople()) {
        payload.amount_people = Number(modal.amountPeople);
      } else if (this.bookingDragNeedsPersonTypes()) {
        payload.amount_adults = Number(modal.amountAdults);
        payload.amount_children = Number(modal.amountChildren);
      } else if (
        modal.listingDetail &&
        this._bookingDragPersonUnit(modal.listingDetail)
      ) {
        payload.amount_people = 1;
      } else {
        payload.amount_people = 0;
        payload.amount_adults = null;
        payload.amount_children = null;
      }
    }
    this._submitBookingDrag(payload, modal);
  },
};
